// Returns a representative photo URL for a Link light rail station.
//
// Each OBA stop name is mapped to a curated Wikipedia article (or, for
// Bellevue Downtown, a specific Commons file) so we never fall back to a
// disambiguation page. Without this, names like "Northgate" resolved to
// the Wikipedia disambiguation article, and earlier heuristics
// occasionally surfaced an unrelated station's photo.
//
// For a `wikiTitle` entry we still fetch the summary at runtime so we
// pick up whatever lead image the article currently uses (Wikipedia
// editors swap these out periodically). Results are cached in-memory for
// the life of the page so we don't hammer the API.

type StationSource =
  | { wikiTitle: string }
  // Bellevue Downtown's Wikipedia article ("Bellevue Transit Center")
  // uses a photo of the bus loop, not the elevated 2 Line platform, so
  // we point at a Commons file directly.
  | { imageUrl: string };

const STATIONS: Record<string, StationSource> = {
  // 1 Line
  "angle lake":              { wikiTitle: "Angle Lake station" },
  "seatac/airport":          { wikiTitle: "SeaTac/Airport station" },
  "tukwila int'l blvd":      { wikiTitle: "Tukwila International Boulevard station" },
  "rainier beach":           { wikiTitle: "Rainier Beach station" },
  "othello":                 { wikiTitle: "Othello station" },
  "columbia city":           { wikiTitle: "Columbia City station" },
  "mount baker":             { wikiTitle: "Mount Baker station" },
  "beacon hill":             { wikiTitle: "Beacon Hill station (Sound Transit)" },
  "sodo":                    { wikiTitle: "SODO station (Sound Transit)" },
  "stadium":                 { wikiTitle: "Stadium station (Sound Transit)" },
  "int'l dist/chinatown":    { wikiTitle: "International District/Chinatown station" },
  "pioneer square":          { wikiTitle: "Pioneer Square station (Sound Transit)" },
  "symphony":                { wikiTitle: "Symphony station (Sound Transit)" },
  "westlake":                { wikiTitle: "Westlake station (Sound Transit)" },
  "capitol hill":            { wikiTitle: "Capitol Hill station" },
  "univ of washington":      { wikiTitle: "University of Washington station" },
  "u district":              { wikiTitle: "U District station" },
  "roosevelt":               { wikiTitle: "Roosevelt station (Sound Transit)" },
  "northgate":               { wikiTitle: "Northgate station (Sound Transit)" },
  "shoreline south/148th":   { wikiTitle: "Shoreline South/148th station" },
  "shoreline north/185th":   { wikiTitle: "Shoreline North/185th station" },
  "mountlake terrace":       { wikiTitle: "Mountlake Terrace station" },
  "lynnwood city center":    { wikiTitle: "Lynnwood City Center station" },
  "kent des moines":         { wikiTitle: "Kent/Des Moines station" },
  "star lake":               { wikiTitle: "Star Lake station" },
  "federal way downtown":    { wikiTitle: "Federal Way Downtown station" },
  // 2 Line
  "judkins park":            { wikiTitle: "Judkins Park station" },
  "mercer island":           { wikiTitle: "Mercer Island station" },
  "south bellevue":          { wikiTitle: "South Bellevue station" },
  "east main":               { wikiTitle: "East Main station" },
  "bellevue downtown": {
    imageUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/First_2_Line_trains_at_Bellevue_Downtown_station_-_April_27%2C_2024.jpg/1280px-First_2_Line_trains_at_Bellevue_Downtown_station_-_April_27%2C_2024.jpg",
  },
  "wilburton":               { wikiTitle: "Wilburton station (Sound Transit)" },
  "spring district":         { wikiTitle: "Spring District station" },
  "belred":                  { wikiTitle: "BelRed station" },
  "overlake village":        { wikiTitle: "Overlake Village station" },
  "redmond technology":      { wikiTitle: "Redmond Technology station" },
  "marymoor village":        { wikiTitle: "Marymoor Village station" },
  "downtown redmond":        { wikiTitle: "Downtown Redmond station" },
};

const cache = new Map<string, string | null>();
const inflight = new Map<string, Promise<string | null>>();

async function fetchSummaryImage(title: string): Promise<string | null> {
  const url = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(
    title,
  )}?redirect=true`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) return null;
  const data = (await res.json()) as {
    type?: string;
    thumbnail?: { source?: string };
    originalimage?: { source?: string };
  };
  // All curated titles resolve to a "standard" article, but guard
  // anyway so a future Wikipedia rename can't surface a disambiguation
  // page's icon as a station photo.
  if (data.type === "disambiguation") return null;
  return data.originalimage?.source ?? data.thumbnail?.source ?? null;
}

export async function fetchStationImage(
  stopName: string,
): Promise<string | null> {
  const key = stopName.trim().toLowerCase();
  if (!key) return null;
  if (cache.has(key)) return cache.get(key) ?? null;
  const existing = inflight.get(key);
  if (existing) return existing;

  const source = STATIONS[key];
  if (!source) {
    cache.set(key, null);
    return null;
  }

  if ("imageUrl" in source) {
    cache.set(key, source.imageUrl);
    return source.imageUrl;
  }

  const p = (async () => {
    try {
      const img = await fetchSummaryImage(source.wikiTitle);
      cache.set(key, img);
      return img;
    } catch {
      // Don't poison the cache on transient network/CORS errors so a
      // later message has a chance to retry.
      return null;
    }
  })();
  inflight.set(key, p);
  try {
    return await p;
  } finally {
    inflight.delete(key);
  }
}
